/** The Activity page's small icons (Paper boards 33, 33b): step kinds, what started a turn, status. */
import { cn } from "@crew/ui/lib/utils";
import {
  AppWindow,
  Bell,
  Check,
  CircleDot,
  Clock,
  FileText,
  Globe,
  PenLine,
  RotateCcw,
  Search,
  Send,
  Stamp,
  SquareCheck,
  SquareTerminal,
  TriangleAlert,
  User,
  X,
  ListTodo,
  ScrollText,
  type LucideIcon,
} from "lucide-react";
import type { RowState } from "./activity-view";

const STEP_ICONS: Record<string, LucideIcon> = {
  search: Search,
  page: Globe,
  browser: AppWindow,
  file: FileText,
  note: PenLine,
  command: SquareTerminal,
  message: Send,
  tasks: ListTodo,
  docs: ScrollText,
  routines: Clock,
  other: CircleDot,
};

export function StepIcon({ kind, className }: { kind: string; className?: string }) {
  const Icon = STEP_ICONS[kind] ?? CircleDot;
  return <Icon aria-hidden className={className} />;
}

const STARTED_ICONS: Record<string, LucideIcon> = {
  person: User,
  routine: Clock,
  task: SquareCheck,
  approval: Stamp,
  nudge: Bell,
  retry: RotateCcw,
  unexpected: TriangleAlert,
};

export function StartedByIcon({ kind, className }: { kind: string; className?: string }) {
  const Icon = STARTED_ICONS[kind] ?? User;
  return <Icon aria-hidden className={className} />;
}

const STATE_LABELS: Record<RowState, string> = {
  working: "Working",
  done: "Done",
  failed: "Failed",
  unexpected: "Unexpected run",
};

/** A row's status at the end of its line: a moss check, a copper dot, a red cross or triangle. */
export function StatusMark({ state, className }: { state: RowState; className?: string }) {
  const label = STATE_LABELS[state];
  if (state === "working") {
    return (
      <span
        role="img"
        aria-label={label}
        className={cn("flex w-4 shrink-0 justify-center", className)}
      >
        <span className="size-2 rounded-full bg-copper ring-[3px] ring-copper/20" />
      </span>
    );
  }
  const Icon = state === "done" ? Check : state === "failed" ? X : TriangleAlert;
  return (
    <span
      role="img"
      aria-label={label}
      className={cn("flex w-4 shrink-0 justify-center", className)}
    >
      <Icon className={cn("size-3.5", state === "done" ? "text-moss" : "text-destructive")} />
    </span>
  );
}
